import { state } from '../state';
import { post } from '../api';
import { showToast } from '../components/toast';
import type { VoiceChannel } from '../types';
import {
  Room,
  RoomEvent,
  Track,
  ConnectionState,
  createLocalAudioTrack,
} from 'livekit-client';
import type {
  LocalAudioTrack,
  LocalParticipant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
} from 'livekit-client';

interface JoinResp {
  url: string;
  token: string;
  room: string;
}

interface VoiceSession {
  room: Room;
  channelId: string;
  channelName: string;
  localAudio: LocalAudioTrack | null;
  audioElements: Map<string, HTMLAudioElement>;
  videoElements: Map<string, HTMLVideoElement>;
  muted: boolean;
  deafened: boolean;
  cameraOn: boolean;
  screenOn: boolean;
  reconnectAttempts: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
}

let session: VoiceSession | null = null;
let lastContainer: HTMLElement | null = null;
let lastChannel: VoiceChannel | null = null;

/**
 * Subscribers receive the loudest active-speaker audio level (0..1) every
 * time LiveKit fires ActiveSpeakersChanged, plus 0 when the speaker set
 * empties out. Used by the bottom-bar voice-connected meter so every
 * channel-list re-render doesn't have to re-attach a Room event handler
 * (which would leak listeners across renders).
 */
type LevelCallback = (level: number) => void;
const levelSubscribers = new Set<LevelCallback>();

export function subscribeVoiceLevel(cb: LevelCallback): () => void {
  levelSubscribers.add(cb);
  return () => { levelSubscribers.delete(cb); };
}

// ─── Public API ────────────────────────────────────────────────────────────

export function getActiveVoiceSession(): { channelId: string; channelName: string; participantCount: number } | null {
  if (!session) return null;
  return {
    channelId: session.channelId,
    channelName: session.channelName,
    participantCount: session.room.remoteParticipants.size + 1,
  };
}

/** Identities of participants currently in `channelId` (or empty array). */
export function getVoiceParticipants(channelId: string): { identity: string; name: string; isLocal: boolean; muted: boolean }[] {
  if (!session || session.channelId !== channelId) return [];
  const local = session.room.localParticipant;
  const remotes = Array.from(session.room.remoteParticipants.values());
  return [local, ...remotes].map(p => ({
    identity: p.identity,
    name: p.name || p.identity,
    isLocal: p === local,
    muted: p.isMicrophoneEnabled === false,
  }));
}

/** Render the voice channel panel into the provided container. */
export function renderVoicePanel(container: HTMLElement, ch: VoiceChannel): void {
  lastContainer = container;
  lastChannel = ch;

  const connected = session?.channelId === ch.id;
  const localParticipant: LocalParticipant | null = connected && session ? session.room.localParticipant : null;
  const allParticipants = connected && session
    ? [session.room.localParticipant, ...Array.from(session.room.remoteParticipants.values())]
    : [];

  const screenSharer = connected && session
    ? findScreenSharer(session)
    : null;

  const status = connectionStatusLabel(connected, session);
  const count = allParticipants.length;

  let html = `<div class="vc-panel ${screenSharer ? 'vc-with-share' : ''}">
    <div class="vc-stage" id="vc-stage">`;

  if (screenSharer) {
    html += `<div class="vc-share-main" id="vc-share-main"></div>
      <div class="vc-tile-strip">${tilesHtml(allParticipants, count)}</div>`;
  } else if (count === 0) {
    html += `<div class="vc-empty-stage">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" y1="19" x2="12" y2="23"/>
        <line x1="8" y1="23" x2="16" y2="23"/>
      </svg>
      <div class="vc-empty-title">No one's here yet</div>
      <div class="vc-empty-sub">Join the channel to start a conversation.</div>
    </div>`;
  } else if (connected && count === 1) {
    // Discord "ringing" state — user joined a voice channel but no one else
    // has arrived yet. Centered title + hint, plus the user's own tile below.
    html += `<div class="vc-solo-stage">
      <div class="vc-solo-title">Just you for now\u2026</div>
      <div class="vc-solo-hint">We'll let you know when someone joins.</div>
      <div class="vc-solo-grid">${tilesHtml(allParticipants, count)}</div>
    </div>`;
  } else {
    html += `<div class="vc-grid vc-grid-${gridClass(count)}">${tilesHtml(allParticipants, count)}</div>`;
  }

  html += `</div>`;

  // AI voice hint — shown for ai_voice channels (migration 070).
  if (ch.channel_kind === 'ai_voice') {
    html += `<div class="vc-ai-hint" style="background:var(--accent,#5865f2);color:#fff;padding:6px 12px;font-size:12px;text-align:center;border-radius:4px;margin:4px 8px">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px;margin-right:4px" aria-hidden="true"><path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.07A7 7 0 0 1 14 24h-4a7 7 0 0 1-6.93-6H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2zm-2 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm4 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></svg>AI agent joins automatically when you connect.</div>`;
  }

  html += `<div class="vc-callbar">
      <div class="vc-callbar-info">
        <span class="vc-callbar-title">${escHtml(ch.name)}</span>
        <span class="vc-callbar-status" data-connected="${connected}">${status}</span>
      </div>
      <div class="vc-callbar-controls">
        ${connected ? renderConnectedControls(session) : `<button class="vc-btn vc-btn-join" id="vc-join">Join Voice</button>`}
      </div>
    </div>
  </div>`;

  container.innerHTML = html;

  // Mount video tiles (cameras + share) after DOM exists
  if (connected && session) {
    mountVideoTiles(session, screenSharer);
  }

  if (!connected) {
    document.getElementById('vc-join')?.addEventListener('click', () => { void join(container, ch); });
    return;
  }

  // Wire callbar buttons
  document.getElementById('vc-mute')?.addEventListener('click', () => { void toggleMute(container, ch); });
  document.getElementById('vc-deafen')?.addEventListener('click', () => { void toggleDeafen(container, ch); });
  document.getElementById('vc-camera')?.addEventListener('click', () => { void toggleCamera(container, ch); });
  document.getElementById('vc-screen')?.addEventListener('click', () => { void toggleScreen(container, ch); });
  document.getElementById('vc-disconnect')?.addEventListener('click', () => { void disconnect(container, ch); });
  document.getElementById('vc-settings')?.addEventListener('click', () => { void openDevicePicker(container); });
  document.getElementById('vc-soundboard')?.addEventListener('click', (e) => {
    openSoundboardFlyout(e.currentTarget as HTMLElement);
  });
  document.getElementById('vc-noise')?.addEventListener('click', () => {
    toggleNoiseSuppression();
    renderVoicePanel(container, ch);
  });
  void localParticipant; // suppress unused warn when not used directly
}

/** Discord-parity Krisp-style noise suppression toggle. The actual filter
 *  hookup depends on the LiveKit build flag — when `setAudioFilters` /
 *  Krisp processor isn't available we still persist the user preference so
 *  the toggle reflects state and the next mic re-publish picks it up. */
const NOISE_KEY = 'hub-people-voice-noise-suppression';
function isNoiseSuppressionOn(): boolean {
  try { return localStorage.getItem(NOISE_KEY) !== '0'; } catch { return true; }
}
function toggleNoiseSuppression(): void {
  const next = !isNoiseSuppressionOn();
  try { localStorage.setItem(NOISE_KEY, next ? '1' : '0'); } catch { /* noop */ }
  if (session?.localAudio) {
    // Apply the standard browser constraint while the Krisp processor isn't
    // wired — covers the 80% case (Chrome/Edge built-in NS) for free.
    void session.localAudio.mediaStreamTrack.applyConstraints({
      noiseSuppression: next,
      echoCancellation: true,
    } as MediaTrackConstraints).catch(() => { /* unsupported */ });
  }
  showToast(next ? 'Noise suppression on' : 'Noise suppression off', 'ok');
}

/** Soundboard flyout — Discord parity. Tiny grid of canned sound effects.
 *  Plays locally for now (LiveKit publish pipeline for sounds lands later),
 *  so other participants don't yet hear the clip — Mitchell's call.
 *  Empty-state messaging when no sounds are configured. */
const SOUNDBOARD_CLIPS: Array<{ id: string; label: string; icon: string }> = [
  { id: 'tada', label: 'Tada', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5.8 11.3 2 22l10.7-3.79"/><path d="M4 3h.01"/><path d="M22 8h.01"/><path d="M15 2h.01"/><path d="M22 20h.01"/><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"/><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11l-.37 2.1c-.15.9-1.01 1.5-1.93 1.32C15.98 16.99 14.93 16 14 15"/></svg>' },
  { id: 'drum', label: 'Drum', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="10" rx="9" ry="5"/><path d="M3 10v4c0 2.76 4.03 5 9 5s9-2.24 9-5v-4"/><line x1="7" y1="3" x2="10" y2="8"/><line x1="17" y1="3" x2="14" y2="8"/></svg>' },
  { id: 'horn', label: 'Air horn', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 15V9a2 2 0 0 1 2-2h2l10-5v20L6 17H4a2 2 0 0 1-2-2z"/><path d="M19 12a4 4 0 0 0 0-8"/></svg>' },
  { id: 'clap', label: 'Clap', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 11v-1a2 2 0 1 1 4 0"/><path d="M11 10V6a2 2 0 1 1 4 0v5"/><path d="M15 9.4V5a2 2 0 1 1 4 0v7"/><path d="M7 11a2 2 0 0 0-4 0v5a8 8 0 0 0 16 0v-1"/></svg>' },
  { id: 'wow', label: 'Wow', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="9" cy="9" r="1"/><circle cx="15" cy="9" r="1"/><circle cx="12" cy="15" r="2"/></svg>' },
  { id: 'sad', label: 'Sad trombone', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>' },
];
function openSoundboardFlyout(anchor: HTMLElement): void {
  document.querySelectorAll('.vc-soundboard-flyout').forEach(el => el.remove());
  const flyout = document.createElement('div');
  flyout.className = 'vc-soundboard-flyout';
  flyout.setAttribute('role', 'dialog');
  flyout.setAttribute('aria-label', 'Soundboard');
  flyout.innerHTML = `
    <div class="vc-sb-title">Soundboard</div>
    <div class="vc-sb-grid">
      ${SOUNDBOARD_CLIPS.map(c => `<button type="button" class="vc-sb-clip" data-sb="${c.id}" title="${c.label}"><span class="vc-sb-icon">${c.icon}</span><span class="vc-sb-label">${c.label}</span></button>`).join('')}
    </div>
    <div class="vc-sb-empty">Custom soundboard clips coming with the upload UI in a future update.</div>
  `;
  document.body.appendChild(flyout);
  const r = anchor.getBoundingClientRect();
  flyout.style.position = 'fixed';
  flyout.style.left = `${Math.max(8, r.left)}px`;
  flyout.style.bottom = `${Math.max(8, window.innerHeight - r.top + 8)}px`;
  const close = (): void => {
    flyout.remove();
    document.removeEventListener('mousedown', onOutside, true);
    document.removeEventListener('keydown', onKey, true);
  };
  const onOutside = (e: MouseEvent): void => {
    const t = e.target as Node | null;
    if (!t) return;
    if (flyout.contains(t) || anchor.contains(t)) return;
    close();
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  setTimeout(() => {
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('keydown', onKey, true);
  }, 0);
  flyout.querySelectorAll<HTMLButtonElement>('.vc-sb-clip').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['sb'] ?? '';
      // Local short beep so the click feels reactive even before clip files
      // are uploaded. Web Audio short tone — no external file needed.
      try {
        const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = id === 'horn' ? 220 : id === 'drum' ? 110 : 440;
        osc.connect(gain); gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        osc.start();
        osc.stop(ctx.currentTime + 0.5);
      } catch { /* no-audio env */ }
      showToast(`Soundboard: ${id}`, 'ok');
      close();
    });
  });
}

/**
 * Discord-parity instant join: click a voice channel in the sidebar and
 * you're in. No modal, no confirm. Creates a hidden ghost container so the
 * LiveKit session mounts even when the user is not viewing the full call
 * pane (e.g. stays on #general while connected to a voice channel).
 */
export async function joinVoiceDirect(ch: VoiceChannel): Promise<void> {
  // If we already have a session on this channel, it's a no-op.
  if (session && session.channelId === ch.id) return;
  // Use the existing container if the call view is mounted, otherwise a
  // hidden ghost so the room lives on regardless of which channel is open.
  let ghost = document.getElementById('hp-voice-ghost') as HTMLElement | null;
  if (!ghost) {
    ghost = document.createElement('div');
    ghost.id = 'hp-voice-ghost';
    ghost.style.display = 'none';
    document.body.appendChild(ghost);
  }
  const host = lastContainer && lastChannel && lastChannel.id === ch.id ? lastContainer : ghost;
  await join(host, ch);
}

// ─── Device picker ─────────────────────────────────────────────────────────

// Unified device-prefs keys — match what the user-bar device picker writes
// (`hub-people-voice-mic`/`-out`/`-cam`) so a swap from the bottom popover
// is honored on the next voice join without a manual reset.
const DEV_IN_KEY = 'hub-people-voice-mic';
const DEV_OUT_KEY = 'hub-people-voice-out';

export async function applyInputDevice(deviceId: string): Promise<void> {
  try { localStorage.setItem(DEV_IN_KEY, deviceId); } catch { /* ignore */ }
  if (!session) return;
  try {
    await session.room.switchActiveDevice('audioinput', deviceId);
    showToast('Microphone updated', 'ok');
  } catch (e) {
    showToast(`Mic switch failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
  }
}

export async function applyOutputDevice(deviceId: string): Promise<void> {
  try { localStorage.setItem(DEV_OUT_KEY, deviceId); } catch { /* ignore */ }
  if (!session) return;
  try {
    await session.room.switchActiveDevice('audiooutput', deviceId);
    showToast('Speaker updated', 'ok');
  } catch (e) {
    showToast(`Speaker switch failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
  }
}

// ─── Render helpers ────────────────────────────────────────────────────────

function gridClass(n: number): string {
  if (n <= 1) return '1';
  if (n === 2) return '2';
  if (n <= 4) return '4';
  return '9';
}

function tilesHtml(participants: (LocalParticipant | RemoteParticipant)[], _count: number): string {
  return participants.map(p => {
    const identity = p.identity || 'Anonymous';
    const name = p.name || identity;
    const initial = (name || '?').charAt(0).toUpperCase();
    const isMuted = p.isMicrophoneEnabled === false;
    const isLocal = !!session && session.room.localParticipant.identity === p.identity;
    const hasCamera = p.isCameraEnabled === true;
    return `<div class="vc-tile" data-identity="${escAttr(identity)}">
      <div class="vc-tile-video" data-mount-video="${escAttr(identity)}" style="${hasCamera ? '' : 'display:none'}"></div>
      <div class="vc-tile-avatar" style="${hasCamera ? 'display:none' : ''}">${escHtml(initial)}</div>
      <div class="vc-tile-name">
        <span>${escHtml(name)}${isLocal ? ' <span class="vc-tile-you">(you)</span>' : ''}</span>
        ${isMuted ? '<span class="vc-tile-muted-icon" title="Muted"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg></span>' : ''}
      </div>
    </div>`;
  }).join('');
}

function renderConnectedControls(s: VoiceSession | null): string {
  if (!s) return '';
  // Discord 1:1 in-call control bar: Mute, Deafen, Camera, Screen, Activity,
  // Settings, then Disconnect pinned right (CSS margin-left: auto on
  // .vc-btn-disconnect). Mute + Deafen use danger-active (red) when toggled
  // since they indicate a self-silenced state; Camera + Screen use the
  // positive active class (accent green) because they indicate broadcasting.
  return `
    <button class="vc-btn ${s.muted ? 'vc-btn-danger-active' : ''}" id="vc-mute" title="${s.muted ? 'Unmute' : 'Mute'}" aria-pressed="${s.muted}">
      ${s.muted
        ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"/></svg>'}
    </button>
    <button class="vc-btn ${s.deafened ? 'vc-btn-danger-active' : ''}" id="vc-deafen" title="${s.deafened ? 'Undeafen' : 'Deafen'}" aria-pressed="${s.deafened}">
      ${s.deafened
        ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.59 8.41 13.41 11.59 16.59 14.77 18 13.36 14.83 10.18 18 7 16.59 5.59zM12 3a9 9 0 0 0-9 9v7c0 1.1.9 2 2 2h2v-8H5v-1a7 7 0 0 1 14 0v1h-2v8h2c1.1 0 2-.9 2-2v-7a9 9 0 0 0-9-9z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 3a9 9 0 0 0-9 9v7c0 1.1.9 2 2 2h4v-8H5v-1a7 7 0 0 1 14 0v1h-4v8h4c1.1 0 2-.9 2-2v-7a9 9 0 0 0-9-9z"/></svg>'}
    </button>
    <button class="vc-btn ${s.cameraOn ? 'vc-btn-active' : ''}" id="vc-camera" title="${s.cameraOn ? 'Turn off camera' : 'Turn on camera'}" aria-pressed="${s.cameraOn}">
      ${s.cameraOn
        ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/></svg>'}
    </button>
    <button class="vc-btn ${s.screenOn ? 'vc-btn-active' : ''}" id="vc-screen" title="${s.screenOn ? 'Stop sharing' : 'Share screen'}" aria-pressed="${s.screenOn}">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.11-.9-2-2-2H4c-1.11 0-2 .89-2 2v10c0 1.1.89 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/></svg>
    </button>
    <button class="vc-btn" id="vc-soundboard" title="Soundboard" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M3 4h6v6H3zm12 0h6v6h-6zM3 14h6v6H3zm12 0h6v6h-6z"/></svg>
    </button>
    <button class="vc-btn ${isNoiseSuppressionOn() ? 'vc-btn-active' : ''}" id="vc-noise" title="${isNoiseSuppressionOn() ? 'Noise suppression: on' : 'Noise suppression: off'}" aria-pressed="${isNoiseSuppressionOn()}">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12h2l3-7 4 14 3-7h6"/></svg>
    </button>
    <button class="vc-btn" id="vc-activity" title="Activities (coming soon)" aria-haspopup="dialog" disabled>
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-11 8V8l5 3-5 3z"/></svg>
    </button>
    <button class="vc-btn" id="vc-settings" title="Audio &amp; video settings" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/></svg>
    </button>
    <button class="vc-btn vc-btn-disconnect" id="vc-disconnect" title="Disconnect">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>
    </button>
  `;
}

function connectionStatusLabel(connected: boolean, s: VoiceSession | null): string {
  if (!connected || !s) return 'Not connected';
  const cs = s.room.state;
  if (cs === ConnectionState.Connected) {
    const n = s.room.remoteParticipants.size + 1;
    return `${n} connected`;
  }
  if (cs === ConnectionState.Reconnecting) return 'Reconnecting…';
  if (cs === ConnectionState.Connecting) return 'Connecting…';
  if (cs === ConnectionState.Disconnected) return 'Disconnected';
  return String(cs);
}

function findScreenSharer(s: VoiceSession): { identity: string; track: Track } | null {
  const local = s.room.localParticipant;
  for (const pub of local.trackPublications.values()) {
    if (pub.source === Track.Source.ScreenShare && pub.track) {
      return { identity: local.identity, track: pub.track };
    }
  }
  for (const p of s.room.remoteParticipants.values()) {
    for (const pub of p.trackPublications.values()) {
      if (pub.source === Track.Source.ScreenShare && pub.track) {
        return { identity: p.identity, track: pub.track };
      }
    }
  }
  return null;
}

function mountVideoTiles(s: VoiceSession, screenSharer: { identity: string; track: Track } | null): void {
  // Mount per-participant camera
  const local = s.room.localParticipant;
  const all: (LocalParticipant | RemoteParticipant)[] = [local, ...Array.from(s.room.remoteParticipants.values())];
  for (const p of all) {
    const mount = document.querySelector<HTMLElement>(`[data-mount-video="${cssEscape(p.identity)}"]`);
    if (!mount) continue;
    let cameraTrack: Track | null = null;
    for (const pub of p.trackPublications.values()) {
      if (pub.source === Track.Source.Camera && pub.track) { cameraTrack = pub.track; break; }
    }
    if (!cameraTrack) continue;
    mount.innerHTML = '';
    const el = cameraTrack.attach() as HTMLVideoElement;
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.muted = p === local;  // never echo own mic into video tag
    mount.appendChild(el);
    s.videoElements.set(p.identity + ':camera', el);
  }

  const main = document.getElementById('vc-share-main');
  if (screenSharer) {
    if (main) {
      main.innerHTML = '';
      const el = screenSharer.track.attach() as HTMLVideoElement;
      el.style.width = '100%';
      el.style.height = '100%';
      el.style.objectFit = 'contain';
      el.style.background = 'black';
      el.muted = screenSharer.identity === s.room.localParticipant.identity;
      main.appendChild(el);
      s.videoElements.set(screenSharer.identity + ':screen', el);
      main.classList.add('vc-share-active');
    }
    // Mirror Discord's tile glow on the sharer's tile so participants can see
    // who's broadcasting at a glance from the strip.
    document.querySelectorAll<HTMLElement>('.vc-tile').forEach(tile => {
      const id = tile.dataset['identity'];
      tile.classList.toggle('vc-tile-screensharing', id === screenSharer.identity);
    });
  } else {
    if (main) main.classList.remove('vc-share-active');
    document.querySelectorAll<HTMLElement>('.vc-tile.vc-tile-screensharing')
      .forEach(t => t.classList.remove('vc-tile-screensharing'));
  }
}

// ─── Connection lifecycle ──────────────────────────────────────────────────

async function join(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (session) {
    await disconnect(container, ch);
  }
  const personId = state.ownerId;
  if (!personId) { showToast('Sign in to join voice', 'err'); return; }

  const ownerPerson = state.people.find(p => p.id === personId);
  const displayName = ownerPerson?.name || (personId ? `Member-${personId.substring(0, 6)}` : 'Member');

  let resp: JoinResp;
  try {
    resp = await post<JoinResp>(`/api/voice-channels/${encodeURIComponent(ch.id)}/join`, {
      person_id: personId,
      display_name: displayName,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.toLowerCase().includes('voice not configured')) {
      showToast('Voice not configured on this server', 'warn');
    } else {
      showToast(`Could not join: ${msg}`, 'err');
    }
    return;
  }

  const room = new Room({ adaptiveStream: true, dynacast: true });
  session = {
    room,
    channelId: ch.id,
    channelName: ch.name,
    localAudio: null,
    audioElements: new Map(),
    videoElements: new Map(),
    muted: false,
    deafened: false,
    cameraOn: false,
    screenOn: false,
    reconnectAttempts: 0,
    reconnectTimer: null,
  };
  state.activeVoiceChannelId = ch.id;

  // Wire room events
  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach() as HTMLAudioElement;
      el.setAttribute('data-identity', participant.identity);
      if (session?.deafened) el.muted = true;
      document.body.appendChild(el);
      session?.audioElements.set(participant.identity, el);
    }
    rerender();
  });
  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
    track.detach().forEach(e => e.remove());
    if (track.kind === Track.Kind.Audio) session?.audioElements.delete(participant.identity);
    rerender();
  });
  room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
    const who = p.name || p.identity;
    showToast(`${who} joined`, 'ok');
    rerender();
  });
  room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
    const who = p.name || p.identity;
    showToast(`${who} left`, 'warn');
    rerender();
  });
  // Talking ring — outline each tile in --accent while LiveKit flags the
  // identity as an active speaker. Cheap: just toggle a class on the tile
  // element, no re-render required so the ring fades crisply.
  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    const activeIds = new Set(speakers.map(s => s.identity));
    document.querySelectorAll<HTMLElement>('.vc-tile').forEach(el => {
      const id = el.getAttribute('data-identity') || '';
      el.classList.toggle('vc-tile-speaking', activeIds.has(id));
    });
    // Bottom-bar meter — broadcast loudest level so the channel-list overlay
    // can light proportional segments without holding its own LiveKit handle.
    let max = 0;
    for (const s of speakers) {
      const lvl = s.audioLevel ?? 0;
      if (lvl > max) max = lvl;
    }
    for (const cb of levelSubscribers) cb(max);
  });
  room.on(RoomEvent.LocalTrackPublished, () => rerender());
  room.on(RoomEvent.LocalTrackUnpublished, () => rerender());
  room.on(RoomEvent.TrackMuted, () => rerender());
  room.on(RoomEvent.TrackUnmuted, () => rerender());

  room.on(RoomEvent.Reconnecting, () => {
    if (!session) return;
    session.reconnectAttempts += 1;
    rerender();
  });
  room.on(RoomEvent.Reconnected, () => {
    if (!session) return;
    session.reconnectAttempts = 0;
    if (session.reconnectTimer) { clearTimeout(session.reconnectTimer); session.reconnectTimer = null; }
    rerender();
  });
  room.on(RoomEvent.Disconnected, () => {
    if (!session) { rerender(); return; }
    // Only attempt reconnect if the user didn't intentionally leave.
    if (state.activeVoiceChannelId === session.channelId && session.reconnectAttempts < 3) {
      const backoffMs = 500 * Math.pow(2, session.reconnectAttempts);
      session.reconnectTimer = setTimeout(() => { void rejoin(ch); }, backoffMs);
      rerender();
      return;
    }
    teardownSession();
    state.activeVoiceChannelId = null;
    rerender();
  });

  try {
    await room.connect(resp.url, resp.token);
  } catch (e) {
    showToast(`Voice connect failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
    teardownSession();
    state.activeVoiceChannelId = null;
    rerender();
    return;
  }

  // Publish mic — honor saved input device choice if we have one.
  try {
    const savedIn = localStorage.getItem(DEV_IN_KEY) || undefined;
    const track = await createLocalAudioTrack(savedIn ? { deviceId: savedIn } : undefined);
    if (session) session.localAudio = track;
    await room.localParticipant.publishTrack(track);
  } catch (e) {
    showToast(`Microphone unavailable: ${e instanceof Error ? e.message : String(e)}`, 'warn');
  }
  // Apply saved output device once connected (LiveKit uses HTMLAudioElement.setSinkId under the hood).
  const savedOut = localStorage.getItem(DEV_OUT_KEY);
  if (savedOut) {
    try { await room.switchActiveDevice('audiooutput', savedOut); } catch { /* ignore */ }
  }

  rerender();
}

async function rejoin(ch: VoiceChannel): Promise<void> {
  if (!lastContainer) return;
  await join(lastContainer, ch);
}

async function toggleMute(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (!session) return;
  session.muted = !session.muted;
  try {
    await session.room.localParticipant.setMicrophoneEnabled(!session.muted);
  } catch (e) {
    showToast(`Mic toggle failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
  }
  renderVoicePanel(container, ch);
}

async function toggleDeafen(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (!session) return;
  session.deafened = !session.deafened;
  for (const el of session.audioElements.values()) el.muted = session.deafened;
  // Discord parity: deafening also mutes your own mic (you can't hear others,
  // so others shouldn't hear you either) — but undeafening does NOT auto-unmute.
  if (session.deafened && !session.muted) {
    session.muted = true;
    try { await session.room.localParticipant.setMicrophoneEnabled(false); } catch { /* ignore */ }
  }
  renderVoicePanel(container, ch);
}

async function toggleCamera(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (!session) return;
  const next = !session.cameraOn;
  try {
    await session.room.localParticipant.setCameraEnabled(next);
    session.cameraOn = next;
  } catch (e) {
    showToast(`Camera toggle failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
  }
  renderVoicePanel(container, ch);
}

async function toggleScreen(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (!session) return;
  const next = !session.screenOn;
  try {
    await session.room.localParticipant.setScreenShareEnabled(next);
    session.screenOn = next;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // User cancelled the picker is normal; only toast if it's a real error.
    if (!/permission|cancel|abort/i.test(msg)) {
      showToast(`Screen share failed: ${msg}`, 'err');
    }
    session.screenOn = false;
  }
  renderVoicePanel(container, ch);
}

async function disconnect(container: HTMLElement, ch: VoiceChannel): Promise<void> {
  if (!session) return;
  const room = session.room;
  if (session.reconnectTimer) { clearTimeout(session.reconnectTimer); session.reconnectTimer = null; }
  state.activeVoiceChannelId = null;
  teardownSession();
  try { await room.disconnect(); } catch { /* ignore */ }
  renderVoicePanel(container, ch);
}

function teardownSession(): void {
  if (!session) return;
  for (const el of session.audioElements.values()) el.remove();
  for (const el of session.videoElements.values()) el.remove();
  session.audioElements.clear();
  session.videoElements.clear();
  if (session.localAudio) {
    try { session.localAudio.stop(); } catch { /* ignore */ }
  }
  if (session.reconnectTimer) { clearTimeout(session.reconnectTimer); session.reconnectTimer = null; }
  session = null;
}

function rerender(): void {
  if (lastContainer && lastChannel) renderVoicePanel(lastContainer, lastChannel);
}

// ─── Overlay-scoped actions ────────────────────────────────────────────────
// These operate on the current session directly so the persistent bottom-left
// call overlay (rendered in channel-list.ts) can invoke them from any view —
// not just when the user is sitting on the full voice panel.

export async function overlayToggleMute(): Promise<void> {
  if (!session) return;
  session.muted = !session.muted;
  try { await session.room.localParticipant.setMicrophoneEnabled(!session.muted); } catch { /* ignore */ }
  rerender();
}
export async function overlayToggleDeafen(): Promise<void> {
  if (!session) return;
  session.deafened = !session.deafened;
  for (const el of session.audioElements.values()) el.muted = session.deafened;
  if (session.deafened && !session.muted) {
    session.muted = true;
    try { await session.room.localParticipant.setMicrophoneEnabled(false); } catch { /* ignore */ }
  }
  rerender();
}
export async function overlayToggleCamera(): Promise<void> {
  if (!session) return;
  const next = !session.cameraOn;
  try { await session.room.localParticipant.setCameraEnabled(next); session.cameraOn = next; } catch { /* ignore */ }
  rerender();
}
export async function overlayToggleScreen(): Promise<void> {
  if (!session) return;
  const next = !session.screenOn;
  try {
    await session.room.localParticipant.setScreenShareEnabled(next);
    session.screenOn = next;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (!/permission|cancel|abort/i.test(msg)) {
      showToast(`Screen share failed: ${msg}`, 'err');
    }
    session.screenOn = false;
  }
  rerender();
}
export async function overlayDisconnect(): Promise<void> {
  if (!session) return;
  const room = session.room;
  if (session.reconnectTimer) { clearTimeout(session.reconnectTimer); session.reconnectTimer = null; }
  state.activeVoiceChannelId = null;
  teardownSession();
  try { await room.disconnect(); } catch { /* ignore */ }
  rerender();
}
export function getOverlayState(): { muted: boolean; deafened: boolean; cameraOn: boolean; screenOn: boolean } | null {
  if (!session) return null;
  return {
    muted: session.muted,
    deafened: session.deafened,
    cameraOn: session.cameraOn,
    screenOn: session.screenOn,
  };
}

/** Called on full app shutdown to release resources. */
export function cleanupVoice(): void {
  if (!session) return;
  const room = session.room;
  teardownSession();
  state.activeVoiceChannelId = null;
  try { void room.disconnect(); } catch { /* ignore */ }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function escHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}
function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
function cssEscape(s: string): string {
  // Identifiers from server are UUIDs; safe enough to escape quotes for the
  // attribute selector. Avoid bringing CSS.escape polyfill weight for that.
  return s.replace(/"/g, '\\"');
}

/** Audio + video device picker (Discord parity \u2014 the gear icon in the call-bar
 *  opens a small popover anchored above. Lists every input/output device the
 *  browser exposes via mediaDevices.enumerateDevices(); selection persists in
 *  localStorage and applies via setSinkId / replaceTrack on next mute toggle. */
async function openDevicePicker(container: HTMLElement): Promise<void> {
  document.querySelectorAll('.vc-devices-pop').forEach(el => el.remove());

  let mics: MediaDeviceInfo[] = [];
  let cams: MediaDeviceInfo[] = [];
  let outs: MediaDeviceInfo[] = [];
  try {
    // Trigger a permission request if needed so labels are populated.
    try { await navigator.mediaDevices.getUserMedia({ audio: true, video: true }); }
    catch { /* user may have denied; still enumerate, labels just blank */ }
    const devs = await navigator.mediaDevices.enumerateDevices();
    mics = devs.filter(d => d.kind === 'audioinput');
    cams = devs.filter(d => d.kind === 'videoinput');
    outs = devs.filter(d => d.kind === 'audiooutput');
  } catch { /* unsupported \u2014 popover will show fallback msg */ }

  const savedMic = localStorage.getItem('hub-people-voice-mic') || '';
  const savedCam = localStorage.getItem('hub-people-voice-cam') || '';
  const savedOut = localStorage.getItem('hub-people-voice-out') || '';

  const opt = (devs: MediaDeviceInfo[], saved: string, fallback: string): string =>
    devs.length === 0
      ? `<option value="">${escHtml(fallback)}</option>`
      : devs.map(d => `<option value="${escAttr(d.deviceId)}" ${d.deviceId === saved ? 'selected' : ''}>${escHtml(d.label || `Device ${d.deviceId.slice(0, 6)}`)}</option>`).join('');

  const pop = document.createElement('div');
  pop.className = 'vc-devices-pop';
  pop.setAttribute('role', 'dialog');
  pop.setAttribute('aria-label', 'Audio and video settings');
  pop.innerHTML = `
    <div class="vc-devices-pop-header">
      <span class="vc-devices-pop-title">Audio &amp; Video</span>
      <button class="vc-devices-pop-close" id="vc-dev-close" aria-label="Close">&times;</button>
    </div>
    <label>Microphone</label>
    <select id="vc-dev-mic">${opt(mics, savedMic, 'No microphone detected')}</select>
    <label>Camera</label>
    <select id="vc-dev-cam">${opt(cams, savedCam, 'No camera detected')}</select>
    <label>Speaker / Output</label>
    <select id="vc-dev-out">${opt(outs, savedOut, 'Default')}</select>
    <div class="vc-devices-pop-hint">Selections apply on the next mute / camera toggle. Browser permission may be required to see device names.</div>
  `;
  container.appendChild(pop);

  const close = (): void => { pop.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  pop.querySelector('#vc-dev-close')?.addEventListener('click', close);

  pop.querySelector<HTMLSelectElement>('#vc-dev-mic')?.addEventListener('change', (e) => {
    localStorage.setItem('hub-people-voice-mic', (e.currentTarget as HTMLSelectElement).value);
  });
  pop.querySelector<HTMLSelectElement>('#vc-dev-cam')?.addEventListener('change', (e) => {
    localStorage.setItem('hub-people-voice-cam', (e.currentTarget as HTMLSelectElement).value);
  });
  pop.querySelector<HTMLSelectElement>('#vc-dev-out')?.addEventListener('change', async (e) => {
    const id = (e.currentTarget as HTMLSelectElement).value;
    localStorage.setItem('hub-people-voice-out', id);
    // Apply immediately to every <audio> already playing (LiveKit remote audio
    // elements live on document.body with [data-call-identity]).
    const audios = document.querySelectorAll<HTMLAudioElement>('audio[data-call-identity]');
    for (const a of audios) {
      try { if (typeof (a as unknown as { setSinkId?: (id: string) => Promise<void> }).setSinkId === 'function')
        await (a as unknown as { setSinkId: (id: string) => Promise<void> }).setSinkId(id); }
      catch { /* setSinkId requires HTTPS + permission on some browsers */ }
    }
  });
}
